
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Settings, RefreshCw, Users, Package, ShoppingCart, DollarSign, AlertTriangle, BarChart3, Download } from "lucide-react";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PremiumMetricCard } from "./premium/PremiumMetricCard";

const AdminDashboard = () => {
  const { user, hasAdminAccess, userRole, loading } = useOptimizedAuth();

  

  const handleRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Alert variant="destructive" className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-semibold mb-2">Access Denied</div>
            <div className="text-sm">
              You don't have permission to access this dashboard.
              {userRole && (
                <div className="mt-1 text-xs">Current role: {userRole}</div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 space-y-8 p-6">
      {/* Premium Header Section */}
      <div className="premium-card">
        <div className="flex items-center justify-between p-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Executive Dashboard
              </h1>
              <div className="px-3 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-xs font-medium border border-emerald-500/20">
                Live
              </div>
            </div>
            <p className="text-muted-foreground">
              Welcome back, {user?.email}! ({userRole})
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh} className="premium-button">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" className="premium-button">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" className="premium-button">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button className="premium-button-primary">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
          </div>
        </div>
      </div>

      {/* Premium Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <PremiumMetricCard
          title="Total Users"
          value="1,234"
          change={10}
          icon={Users}
          gradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        
        <PremiumMetricCard
          title="Total Products"
          value="567"
          change={5}
          icon={Package}
          gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
        />
        
        <PremiumMetricCard
          title="Total Orders"
          value="890"
          change={15}
          icon={ShoppingCart}
          gradient="bg-gradient-to-br from-violet-500 to-violet-600"
        />
        
        <PremiumMetricCard
          title="Revenue"
          value="$45,231"
          change={20}
          icon={DollarSign}
          gradient="bg-gradient-to-br from-amber-500 to-amber-600"
        />
      </div>

      {/* Enhanced Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="premium-card hover:shadow-elegant transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-primary/80">
                <Package className="h-4 w-4 text-white" />
              </div>
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full justify-start premium-button-primary group">
              <Package className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              Manage Products
            </Button>
            <Button className="w-full justify-start premium-button group" variant="outline">
              <Users className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              Manage Users
            </Button>
            <Button className="w-full justify-start premium-button group" variant="outline">
              <ShoppingCart className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              View Orders
            </Button>
          </CardContent>
        </Card>

        <Card className="premium-card hover:shadow-elegant transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <span className="font-medium">New user registered</span>
                <span className="text-muted-foreground text-xs">2 min ago</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <span className="font-medium">Order #1234 completed</span>
                <span className="text-muted-foreground text-xs">5 min ago</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <span className="font-medium">Product updated</span>
                <span className="text-muted-foreground text-xs">10 min ago</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card hover:shadow-elegant transition-all duration-300">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600">
                <Settings className="h-4 w-4 text-white" />
              </div>
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <span className="font-medium">Database</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-emerald-600 font-medium">Online</span>
                </div>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <span className="font-medium">API</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-emerald-600 font-medium">Online</span>
                </div>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <span className="font-medium">Storage</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="text-emerald-600 font-medium">Online</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
